import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable per-route SEO component.
 *
 * The static tags in /public/index.html act as the sensible default for
 * every route (title, description, OG image, JSON-LD org+site+FAQ).
 * <Seo /> lets individual pages override the pieces that matter for that
 * page — primarily title, description, canonical, and (optionally)
 * additional structured data via `jsonLd`.
 *
 * Kept intentionally minimal — one component, no extra abstractions.
 */
const DEFAULT_DESCRIPTION =
  "EVIQ is the independent battery-health intelligence platform for Tesla, Ford, and Volvo/Polestar owners. Track State of Health, forecast degradation, protect your warranty, and generate cryptographically-signed Battery Passports.";

export const Seo = ({
  title,
  description,
  canonical,
  ogImage = "https://eviq.pro/og-image.png",
  noindex = false,
  jsonLd,
}) => {
  const fullTitle = title
    ? (title.includes("EVIQ") ? title : `${title} · EVIQ`)
    : "EVIQ — Tesla battery health, warranty, and Digital Battery Passport";
  const desc = description || DEFAULT_DESCRIPTION;
  const url = canonical || (typeof window !== "undefined" ? window.location.href : "https://eviq.pro/");

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
